import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useCreateCFLocation } from '@/hooks/use-cf-locations';
import { useIndustry } from '@/hooks/use-industry';
import { handleApiError } from '@/lib/api-client';
import type { CFLocation, CFLocationType, Industry } from '@/lib/types';

const DEFAULT_TYPES: Record<Industry, CFLocationType> = {
  field_ops: 'job_site',
  facility_ops: 'client_facility',
  training_ops: 'training_venue',
  consultant: 'client_facility',
  hybrid: 'job_site',
};

const LOCATION_TYPES: { value: CFLocationType; label: string }[] = [
  { value: 'job_site', label: 'Project Site' },
  { value: 'client_facility', label: 'Client Facility' },
  { value: 'training_venue', label: 'Training Venue' },
  { value: 'depot', label: 'Depot' },
  { value: 'office', label: 'Office' },
  { value: 'client_hq', label: 'Client HQ' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'other', label: 'Other' },
];

interface AddLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (location: CFLocation) => void;
}

export function AddLocationDialog({
  open,
  onOpenChange,
  onCreated,
}: AddLocationDialogProps) {
  const { industry, labels } = useIndustry();
  const createLocation = useCreateCFLocation();
  const [name, setName] = useState('');
  const [type, setType] = useState<CFLocationType>(DEFAULT_TYPES[industry]);
  const [error, setError] = useState('');

  const close = () => {
    setName('');
    setType(DEFAULT_TYPES[industry]);
    setError('');
    onOpenChange(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setError('');
    try {
      const location = await createLocation.mutateAsync({ name: name.trim(), type });
      onCreated(location);
      close();
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => nextOpen ? onOpenChange(true) : close()}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New {labels.location.toLowerCase()}</DialogTitle>
            <DialogDescription>
              Create a {labels.location.toLowerCase()} without leaving this form.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div data-field-container className="space-y-2">
              <Label htmlFor="new-location-name">Name *</Label>
              <Input
                id="new-location-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={(value) => setType(value as CFLocationType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>Cancel</Button>
            <Button type="submit" disabled={createLocation.isPending}>
              {createLocation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create {labels.location.toLowerCase()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
